import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ResponsiveContainer from './ResponsiveContainer';

function PersonalDetailsPage({ personalDetails, setPersonalDetails, t }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: personalDetails.firstName || '',
    lastName: personalDetails.lastName || '',
    phone: personalDetails.phone || '+316',
    streetName: personalDetails.streetName || '',
    houseNumber: personalDetails.houseNumber || '',
    houseNumberAddition: personalDetails.houseNumberAddition || '',
    postalCode: personalDetails.postalCode || '',
    city: personalDetails.city || '',
    dateOfBirth: personalDetails.dateOfBirth || '',
    companyName: personalDetails.companyName || '',
    email: personalDetails.email || '',
    invoiceEmail: personalDetails.invoiceEmail || '',
  });
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = ['firstName', 'lastName', 'email', 'invoiceEmail', 'phone', 'streetName', 'houseNumber', 'postalCode', 'city', 'dateOfBirth', 'email'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      alert(t('pleaseCompleteRequiredFields') + ': ' + 
        missingFields.map(field => t(field)).join(', '));
      return;
    }
    
    // Update personal details in parent state
    setPersonalDetails({
      ...personalDetails,
      ...formData
    });
    
    // Navigate to membership plan page
    navigate('/membership-plan');
  };
  
  const handleBack = () => {
    // Save current form data before navigating back
    setPersonalDetails({
      ...personalDetails,
      ...formData
    });
    navigate('/email-input');
  };
  
  return (
    <ResponsiveContainer>
      <div className="min-h-[calc(100vh-100px)] bg-gray-100 py-6 px-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-3xl mx-auto">
          <h2 className="text-2xl font-semibold mb-6">{t('personalDetails')}</h2>
          <p className="text-gray-600 mb-6">{t('pleaseFillPersonalDetails')}</p>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Personal Information */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('firstName')} *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('lastName')} *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('phone')} *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  pattern="\+3[0-9]{10,11}"
                  placeholder="+316XXXXXXXX"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('enterAdminEmailAddress')} *
                </label>
                <input
                  type="email"
                  name="invoiceEmail"
                  value={formData.invoiceEmail}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('enterAgendaEmailAddress')} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('dateOfBirth')} *
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companyName')}
                </label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              {/* Address Information */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('streetName')} *
                </label>
                <input
                  type="text"
                  name="streetName"
                  value={formData.streetName}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('houseNumber')} *
                  </label>
                  <input
                    type="text"
                    name="houseNumber"
                    value={formData.houseNumber}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('houseNumberAddition')}
                  </label>
                  <input
                    type="text"
                    name="houseNumberAddition"
                    value={formData.houseNumberAddition}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('postalCode')} *
                </label>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('city')} *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>
            
            <div className="flex justify-between pt-4">
              <button
                type="button"
                onClick={handleBack}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                {t('back')}
              </button>
              
              <button
                type="submit"
                className="px-4 py-2 bg-[#d0a949] text-white rounded-md hover:bg-[#a6873a]"
              >
                {t('next')}
              </button>
            </div>
            
            <p className="text-sm text-gray-500 mt-4">
              * {t('requiredFields')}
            </p>
          </form>
        </div>
      </div>
    </ResponsiveContainer>
  );
}

export default PersonalDetailsPage;